import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Menu,
  Segment,
  Header,
  Icon,
  Container
} from 'semantic-ui-react';
import { SemanticICONS } from 'semantic-ui-react/dist/commonjs/generic';

// Import the real components
import Companies from '../../features/venues/Companies';
import CompanyVenues from './CompanyVenues';
import VenueAssets from './VenueAssets';
import SportAssetCalendar from './SportAssetCalendar';
import Reports from '../reporting/Reports';

type MenuItem = {
  name: string;
  icon: SemanticICONS;
  key: string;
};

export default function VenueScheduler() {
  const { t } = useTranslation();
  const [activeItem, setActiveItem] = useState('Sport Asset Calendar');
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  React.useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const menuItems: MenuItem[] = [
    { name: 'Sport Asset Calendar', icon: 'calendar alternate outline', key: 'navigation.assetCalendar' },
    { name: 'Companies',            icon: 'building',                   key: 'navigation.companies'    },
    { name: 'Company Venues',       icon: 'map marker alternate',       key: 'navigation.venues'       },
    { name: 'Venue Assets',         icon: 'cubes',                      key: 'navigation.assets'       },
    { name: 'Reports',              icon: 'chart bar',                  key: 'navigation.reports'      },
  ];

  const renderContent = () => {
    switch (activeItem) {
      case 'Sport Asset Calendar': return <SportAssetCalendar />;
      case 'Companies':            return <Companies />;
      case 'Company Venues':       return <CompanyVenues />;
      case 'Venue Assets':         return <VenueAssets />;
      case 'Reports':              return <Reports />;
      default:                     return null;
    }
  };

  return (
    <div style={{
      height: '100vh',
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden'
    }}>
      {/* Header */}
      <Segment
        inverted
        style={{
          margin: 0,
          borderRadius: 0,
          backgroundImage: 'linear-gradient(135deg, rgb(24,42,115) 0%, rgb(33,138,174) 69%, rgb(32,167,172) 89%)',
          padding: isMobile ? '0.8rem 1rem' : '1rem 1.5rem',
          flexShrink: 0
        }}
      >
        <Header as={isMobile ? 'h3' : 'h2'} inverted style={{ margin: 0 }}>
          <Icon name="calendar" />
          <Header.Content>{t('app.title')}</Header.Content>
        </Header>
      </Segment>

      {/* Navigation */}
      {isMobile ? (
        <div style={{
          display: 'flex',
          background: '#fff',
          borderBottom: '1px solid #d4d4d5',
          boxShadow: '0 2px 4px rgba(0,0,0,0.08)',
          flexShrink: 0,
        }}>
          {menuItems.map(item => {
            const active = activeItem === item.name;
            return (
              <button
                key={item.name}
                onClick={() => setActiveItem(item.name)}
                style={{
                  flex: '1 1 0',
                  minWidth: 0,
                  background: 'none',
                  border: 'none',
                  borderBottom: active ? '2px solid #2185d0' : '2px solid transparent',
                  color: active ? '#2185d0' : '#666',
                  padding: '0.55rem 0.2rem 0.45rem',
                  cursor: 'pointer',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: '0.15rem',
                  transition: 'color 0.15s',
                }}
              >
                <Icon
                  name={item.icon}
                  style={{ margin: 0, fontSize: '1.15rem' }}
                />
                <span style={{
                  fontSize: '0.62rem',
                  fontWeight: active ? 700 : 500,
                  lineHeight: 1.1,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  maxWidth: '100%',
                  textAlign: 'center',
                }}>
                  {t(item.key)}
                </span>
              </button>
            );
          })}
        </div>
      ) : (
        /* Desktop: original Semantic UI Menu */
        <Menu
          pointing
          style={{
            margin: 0,
            borderRadius: 0,
            padding: '0 1rem',
            flexShrink: 0,
          }}
        >
          {menuItems.map(item => (
            <Menu.Item
              key={item.name}
              name={item.name}
              active={activeItem === item.name}
              onClick={() => setActiveItem(item.name)}
            >
              <Icon name={item.icon} />
              {t(item.key)}
            </Menu.Item>
          ))}
        </Menu>
      )}

      {/* Content Area */}
      <div style={{
        flex: 1,
        overflow: 'auto',
        backgroundColor: '#f5f5f5'
      }}>
        <Container fluid style={{ maxWidth: '100%', padding: 0 }}>
          {renderContent()}
        </Container>
      </div>
    </div>
  );
}